import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Divider, Grid, IconButton, InputAdornment,
  MenuItem, Paper, Select, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import LockClockIcon from "@mui/icons-material/LockClock";
import PaymentsIcon from "@mui/icons-material/Payments";
import ReplayIcon from "@mui/icons-material/Replay";
import CallIcon from "@mui/icons-material/Call";
import API from "../../API";

/*
 * Shared base for the Home Moving and Intercity panel sections.
 * Standalone: reads only the marketplace endpoints; shares no state or flow
 * with the legacy order screens. Row click opens a dedicated detail PAGE.
 */

export const LIST_VEHICLE_LABELS = {
  doblo: "Doblo", panel_van: "Panelvan", pickup_truck: "Kamyonet",
  "6_wheel": "6 Teker Kamyon", "8_wheel": "8 Teker Kamyon", "10_wheel": "10 Teker Kamyon", lorry: "Tır",
  "Panel Van": "Panelvan", "Pickup Truck": "Kamyonet", "6-Wheel Truck": "6 Teker Kamyon",
  "8-Wheel Truck": "8 Teker Kamyon", "10-Wheel Truck": "10 Teker Kamyon", "6-Wheel": "6 Teker",
  "8-Wheel": "8 Teker", "10-Wheel": "10 Teker", Lorry: "Tır",
};

// An open request takes offers until its publication period ends; the stored
// quote window can be shorter (overnight requests) and is only a fallback.
export const offerDeadline = (request) => request?.publicationEndsAt || request?.quoteWindowEndsAt || null;

export function remainingLabel(endsAt) {
  if (!endsAt) return null;
  const ms = new Date(endsAt).getTime() - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return null;
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `${minutes} dk kaldı`;
  const hours = Math.floor(minutes / 60);
  return `${hours} sa ${minutes % 60} dk kaldı`;
}

export const STATUS_LABELS = {
  quotes_open: "Teklifler açık",
  quotes_closed_contacted: "Teklif süresi doldu · iletişim devam ediyor",
  mover_selected: "Nakliyeci seçildi",
  driver_selected: "Sürücü seçildi",
  contract_pending: "Sözleşme bekliyor",
  contract_signed: "Sözleşme imzalandı",
  completed: "Tamamlandı",
  not_completed: "Gerçekleşmedi",
  cancelled: "İptal edildi",
  expired: "Süresi doldu",
  auto_closed: "Otomatik kapandı",
  closed: "Kapandı",
  worked_with: "Çalışma gerçekleşti",
  expired_no_selection: "Süresi doldu",
};
export const OUTCOME_LABELS = {
  worked_with_driver: "Sürücüyle çalıştı",
  worked_with_selected_mover: "Nakliyeciyle çalıştı",
  worked_with_mover: "Nakliyeciyle çalıştı",
  delivery_did_not_happen: "Taşıma gerçekleşmedi",
  move_did_not_happen: "Taşınma gerçekleşmedi",
  move_not_completed: "Taşınma gerçekleşmedi",
  customer_cancelled: "Müşteri iptal etti",
  expired_no_selection: "Seçim yapılmadan süresi doldu",
  auto_closed_no_outcome: "Sonuç bildirilmeden kapandı",
  contacted_window_elapsed: "İletişim süresi doldu",
  admin_closure: "Yönetici kapattı",
};
export const STATUS_COLORS = {
  quotes_open: "info",
  quotes_closed_contacted: "warning",
  mover_selected: "primary",
  driver_selected: "primary",
  contract_pending: "warning",
  contract_signed: "success",
  completed: "success",
  not_completed: "error",
  cancelled: "error",
  expired: "default",
  auto_closed: "default",
};
export const HOLD_LABELS = { held: "Blokede", captured: "Tahsil edildi", released: "İade edildi", refunded: "Geri ödendi" };

const FILTERS = [
  { value: null, label: "Tümü" },
  { value: ["quotes_open"], label: "Teklifler açık" },
  { value: null, contactFilter: "quotes_closed", label: "Teklif süresi doldu" },
  { value: null, contactFilter: "selected", label: "Sürücü seçildi" },
  { value: null, contactFilter: "contacted", label: "İletişime geçildi" },
  { value: ["mover_selected", "driver_selected", "contract_pending", "contract_signed"], label: "Devam eden" },
  { value: ["completed"], label: "Tamamlanan" },
  { value: ["cancelled", "not_completed", "expired", "auto_closed"], label: "Kapanan" },
];
const RANGES = [
  { value: 7, label: "Son 7 gün" },
  { value: 30, label: "Son 30 gün" },
  { value: 90, label: "Son 90 gün" },
  { value: 0, label: "Tüm zamanlar" },
  { value: "custom", label: "Özel Aralık" },
];

export const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("tr-TR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};
export const money = (value, currency) => `${Number(value || 0).toLocaleString("tr-TR")} ${currency || "TL"}`;

const STATUS_PILL_STYLES = {
  success: { backgroundColor: "#DCFCE7", color: "#15803D" },
  warning: { backgroundColor: "#FEF3C7", color: "#B45309" },
  error: { backgroundColor: "#FEE2E2", color: "#B91C1C" },
  default: { backgroundColor: "#EEF2FF", color: "#4F46E5" },
};
export function StatusChip({ status }) {
  const tone = ["completed", "contract_signed", "worked_with"].includes(status)
    ? "success"
    : ["contract_pending", "mover_selected", "driver_selected"].includes(status)
      ? "warning"
      : ["cancelled", "not_completed"].includes(status)
        ? "error"
        : "default";
  return (
    <span
      style={{
        ...STATUS_PILL_STYLES[tone],
        display: "inline-block",
        padding: "4px 12px",
        borderRadius: 8,
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      {STATUS_LABELS[status] || status || "-"}
    </span>
  );
}

function StatCard({ label, value, hint, color, icon }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, height: "100%", display: "flex", flexDirection: "column", gap: 0.4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.4, color: "text.secondary", textTransform: "uppercase" }}>{label}</Typography>
        {icon ? <Box sx={{ color: color || "primary.main", display: "flex" }}>{icon}</Box> : null}
      </Stack>
      <Typography variant="h5" sx={{ fontWeight: 800, color: color || "text.primary" }}>{value}</Typography>
      {hint ? <Typography variant="caption" color="text.secondary">{hint}</Typography> : null}
    </Paper>
  );
}

function StatusBar({ orders }) {
  const total = Math.max(1, orders.total);
  const parts = [
    { value: orders.open, color: "#3b9dd7", label: "Açık" },
    { value: orders.contactedWindow || 0, color: "#D99025", label: "İletişim devam" },
    { value: orders.inProgress, color: "#7658C7", label: "Devam" },
    { value: orders.completed, color: "#15A879", label: "Tamamlandı" },
    { value: orders.cancelled, color: "#C64545", label: "İptal" },
    { value: orders.expired, color: "#9AA7B4", label: "Süresi doldu" },
  ];
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.4, color: "text.secondary", textTransform: "uppercase" }}>Talep Dağılımı</Typography>
      <Box sx={{ display: "flex", height: 10, borderRadius: 5, overflow: "hidden", my: 1.2, backgroundColor: "#EEF2F6" }}>
        {parts.filter((part) => part.value > 0).map((part) => (
          <Box key={part.label} sx={{ width: `${(part.value / total) * 100}%`, backgroundColor: part.color }} />
        ))}
      </Box>
      <Stack direction="row" spacing={2} flexWrap="wrap">
        {parts.map((part) => (
          <Stack key={part.label} direction="row" spacing={0.6} alignItems="center">
            <Box sx={{ width: 9, height: 9, borderRadius: "50%", backgroundColor: part.color }} />
            <Typography variant="caption" color="text.secondary">{part.label}: <b>{part.value}</b></Typography>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}

export default function MarketplaceBase({ serviceType, title }) {
  const basePath = serviceType === "intercity" ? "/intercity" : "/home-moving";
  const [items, setItems] = useState(null);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState(0);
  const [range, setRange] = useState(30);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [search, setSearch] = useState("");
  const [pickupCity, setPickupCity] = useState("");
  const [deliveryCity, setDeliveryCity] = useState("");
  const [page, setPage] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback((nextPage = 0) => {
    setLoading(true);
    API.fetchClient("panel/getmarketplacerequests", {
      serviceType,
      statusFilter: FILTERS[filter].value || undefined,
      contactFilter: FILTERS[filter].contactFilter,
      days: range === "custom" ? undefined : range || undefined,
      startDate: range === "custom" && startDate ? startDate : undefined,
      endDate: range === "custom" && endDate ? endDate : undefined,
      search: search.trim() || undefined,
      pickupCity: pickupCity.trim() || undefined,
      deliveryCity: deliveryCity.trim() || undefined,
      page: nextPage,
    }).then((result) => {
      setLoading(false);
      if (!result?.items) return;
      setItems(result.items);
      setHasNext(Boolean(result.hasNext));
      setPage(nextPage);
    });
  }, [serviceType, filter, range, startDate, endDate, search, pickupCity, deliveryCity]);

  const loadStats = useCallback(() => {
    API.fetchClient("panel/getmarketplacestats", { serviceType, days: range === "custom" ? undefined : range || undefined, startDate: range === "custom" && startDate ? startDate : undefined, endDate: range === "custom" && endDate ? endDate : undefined })
      .then((result) => { if (result?.orders) setStats(result); });
  }, [serviceType, range, startDate, endDate]);

  useEffect(() => { load(0); }, [serviceType, filter, range, startDate, endDate]);
  useEffect(() => { loadStats(); }, [loadStats]);

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, pb: { xs: 8, md: 6 }, flexShrink: 0 }}>
      <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>{title}</Typography>
          <Typography variant="body2" color="text.secondary">Pazaryeri talepleri, teklifler ve finans özeti</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Select size="small" value={range} onChange={(event) => setRange(event.target.value)}>
            {RANGES.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
          </Select>
          {range === "custom" ? (
            <>
              <TextField size="small" type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
              <TextField size="small" type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
            </>
          ) : null}
          <IconButton onClick={() => { load(page); loadStats(); }}><RefreshIcon /></IconButton>
        </Stack>
      </Stack>

      {stats ? (
        <>
          <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
            <Grid item xs={6} sm={3} lg={1.7}><StatCard label="Toplam" value={stats.orders.total} /></Grid>
            <Grid item xs={6} sm={3} lg={1.7}><StatCard label="Açık" value={stats.orders.open} color="#3b9dd7" /></Grid>
            <Grid item xs={6} sm={3} lg={1.7}><StatCard label="Devam eden" value={stats.orders.inProgress} color="#7658C7" /></Grid>
            <Grid item xs={6} sm={3} lg={1.7}><StatCard label="Tamamlanan" value={stats.orders.completed} color="#15A879" /></Grid>
            <Grid item xs={6} sm={3} lg={1.7}><StatCard label="İptal / olmadı" value={stats.orders.cancelled} color="#C64545" /></Grid>
            <Grid item xs={6} sm={3} lg={1.7}><StatCard label="Süresi dolan" value={stats.orders.expired} color="#9AA7B4" /></Grid>
            <Grid item xs={6} sm={3} lg={1.8}><StatCard label="Aramalar" value={`${stats.contacts.feeCaptured}/${stats.contacts.total}`} hint="Ücret tahsilli / toplam iletişim" icon={<CallIcon fontSize="small" />} /></Grid>
          </Grid>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={12} md={3}><StatCard label="Blokedeki Tutar" value={money(stats.finance.heldNow)} hint={`${stats.finance.heldCount} aktif bloke · kapanışta sürücüye iade edilir`} color="#A85A10" icon={<LockClockIcon fontSize="small" />} /></Grid>
            <Grid item xs={12} md={3}><StatCard label="Kazanılan (Tahsil)" value={money(stats.finance.earned)} hint={`${stats.finance.earnedCount} bağlantı ücreti tahsil edildi`} color="#15A879" icon={<PaymentsIcon fontSize="small" />} /></Grid>
            <Grid item xs={12} md={3}><StatCard label="Sürücüye İade" value={money(stats.finance.releasedBack)} hint="Kapanan taleplerde çözülen blokeler" color="#3b9dd7" icon={<ReplayIcon fontSize="small" />} /></Grid>
            <Grid item xs={12} md={3}><StatCard label="Geri Ödenen" value={money(stats.finance.refunded)} hint="Ölü iletişim iadeleri" color="#C64545" icon={<ReplayIcon fontSize="small" />} /></Grid>
          </Grid>
          <Box sx={{ mb: 2 }}><StatusBar orders={stats.orders} /></Box>
        </>
      ) : null}

      <Stack direction="row" useFlexGap spacing={1} sx={{ mb: 1.5, flexWrap: "wrap", alignItems: "center", rowGap: 1 }}>
        {FILTERS.map((item, index) => (
          <Chip
            key={item.label}
            label={item.label}
            color={index === filter ? "primary" : "default"}
            variant={index === filter ? "filled" : "outlined"}
            onClick={() => setFilter(index)}
          />
        ))}
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          sx={{ width: { xs: "100%", sm: "auto" } }}
          placeholder="Ad / telefon / adres / talep no"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => { if (event.key === "Enter") load(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <TextField size="small" placeholder="Alış ili" value={pickupCity} onChange={(event) => setPickupCity(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") load(0); }} />
        <TextField size="small" placeholder="Teslimat ili" value={deliveryCity} onChange={(event) => setDeliveryCity(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") load(0); }} />
        <Button variant="outlined" size="small" onClick={() => load(0)}>Ara</Button>
      </Stack>

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box sx={{ overflowX: "auto" }}>
        {loading && !items ? <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box> : (
          <Table size="small">
            <TableHead sx={{ backgroundColor: "#F7FAFC" }}>
              <TableRow>
                <TableCell>Talep ID</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell>Tür</TableCell>
                <TableCell>Müşteri</TableCell>
                <TableCell>Rota</TableCell>
                <TableCell align="center">Teklif</TableCell>
                <TableCell align="center">İletişime Geçilen</TableCell>
                <TableCell>Oluşturulma</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(items || []).map((item) => (
                <TableRow hover key={item.requestId} sx={{ cursor: "pointer" }} onClick={() => window.open(`${basePath}/${item.requestId}`, "_blank", "noopener,noreferrer")}>
                  <TableCell>
                    <Typography component="a" href={`${basePath}/${item.requestId}`} target="_blank" rel="noopener noreferrer" onClick={(event) => event.stopPropagation()} variant="body2" fontWeight={800} sx={{ color: "#0F172A", whiteSpace: "nowrap" }}>
                      #{String(item.requestId).slice(-8).toUpperCase()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <StatusChip status={item.status} />
                    {item.status === "quotes_open" && remainingLabel(offerDeadline(item)) ? (
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.4 }}>
                        {item.quoteCount ? `${item.quoteCount} teklif` : "Henüz teklif yok"} · {remainingLabel(offerDeadline(item))}
                      </Typography>
                    ) : item.status === "quotes_open" ? (
                      <Typography variant="caption" display="block" sx={{ mt: 0.4, color: "#B91C1C" }}>
                        Teklif süresi doldu
                      </Typography>
                    ) : item.status === "auto_closed" && OUTCOME_LABELS[item.closureReason] ? (
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.4 }}>
                        {OUTCOME_LABELS[item.closureReason]}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {item.mode === "one_item"
                        ? "Parça Eşya"
                        : item.mode === "larger_load"
                          ? (LIST_VEHICLE_LABELS[item.requestedVehicle] || item.requestedVehicle || "Büyük yük")
                          : "Evden Eve"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{item.customerName}</Typography>
                    <Typography variant="caption" color="text.secondary">{item.customerPhone}</Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Typography variant="body2" noWrap fontWeight={600}>{String(item.route || "-").replace(/->/g, "→")}</Typography>
                  </TableCell>
                  <TableCell align="center">{item.quoteCount}</TableCell>
                  <TableCell align="center">
                    <Chip size="small" variant="outlined" color={item.contactCount > 0 ? "success" : "default"} label={`${item.contactCount}/${item.maxContacts || 3}`} />
                  </TableCell>
                  <TableCell>{formatDate(item.createdAt)}</TableCell>
                </TableRow>
              ))}
              {items && items.length === 0 ? (
                <TableRow><TableCell colSpan={8}><Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>Bu filtrede talep yok.</Typography></TableCell></TableRow>
              ) : null}
            </TableBody>
          </Table>
        )}
        </Box>
        <Divider />
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ p: 1 }}>
          <Button size="small" disabled={page === 0 || loading} onClick={() => load(page - 1)}>Önceki</Button>
          <Button size="small" disabled={!hasNext || loading} onClick={() => load(page + 1)}>Sonraki</Button>
        </Stack>
      </Paper>
    </Box>
  );
}
